import get from 'lodash.get';
import { createSelector } from 'reselect';

// selectors
import {
  getPageFirstCardData,
  getPageLabels,
  getPageFeatureFlags,
  getPageAllowedLists
} from 'src/universal/redux/page';
import { getPdpCardShortName } from 'page_modules/business-pdp/redux';
import { getIsSummitCard } from 'page_modules/business-pdp/redux/index.js';
import { getCardColorInfoList } from 'src/universal/redux/cardColors';

// utils
import parseArrayToObject from 'src/universal/utils/parseArrayToObject';
import { decideCardArtUrl } from 'src/universal/utils/cardColorChoices';

const isDeltaPlatinum = createSelector(
  [getPdpCardShortName, getPageFeatureFlags],
  (pdpShortName, pageFeatureFlags) => {
    const isDeltaPlat =
      pdpShortName === 'platinum-delta-skymiles' && pageFeatureFlags.showDeltaLto;
    return isDeltaPlat;
  }
);

export const getIsGoldCard = createSelector([getPdpCardShortName], pdpShortName => {
  return pdpShortName === 'gold';
});

export const getIsPlatinumCard = createSelector(
  [getPdpCardShortName],
  pdpShortName => {
    return pdpShortName === 'platinum';
  }
);

export const getIsDeltaReserve = createSelector(
  [getPdpCardShortName],
  pdpShortName => pdpShortName === 'delta-reserve'
);

const useFullScreenHero = createSelector(
  [getPdpCardShortName, getPageAllowedLists],
  (pdpShortName, allowedlists) => {
    const isCardInList =
      (get(allowedlists, 'pdpFullScreenHero') || []).indexOf(pdpShortName) > -1;
    return isCardInList;
  }
);

export const getShouldShowMobileHeader = createSelector(
  [getIsGoldCard, getIsPlatinumCard, getIsDeltaReserve],
  (isGoldCard, isPlatinumCard, isDeltaReserveCard) => {
    return !isGoldCard && !isPlatinumCard && !isDeltaReserveCard;
  }
);

export const needFullPageHero = createSelector(
  [getIsSummitCard, isDeltaPlatinum, useFullScreenHero],
  (isSummitCard, isDeltaPlatFullPage, isOnFullPageList) => {
    return isSummitCard || isDeltaPlatFullPage || isOnFullPageList;
  }
);

export const getCardHeadline = createSelector(
  [getPageFirstCardData],
  cardData =>
    cardData?.cardHeadlines?.heroTitle ?? cardData?.cardHeadlines?.longTitle ?? ''
);

export const getCardAltText = createSelector(
  [getPageFirstCardData],
  cardData => cardData?.cardHeadlines?.summitCardAlt ?? ''
);

export const getCardImageUrl = createSelector(
  [getPageFirstCardData, getCardColorInfoList],
  (cardData, colorInfoList) => {
    const colorInfo = colorInfoList.find(
      color => color.shortName === cardData.shortName
    );
    const cardArt = cardData?.alternativeCardArt ?? cardData?.appAssets?.cardArt;
    return decideCardArtUrl(cardArt, colorInfo, cardData.shortName);
  }
);
export const getCardImageFallback = createSelector(
  [getPageFirstCardData],
  cardData => {
    const cardArt = cardData?.appAssets?.cardArt;
    return cardArt;
  }
);

export const getAnnualFeeText = createSelector(
  [getPageFirstCardData, getPageLabels],
  (cardData, pageLabels) => `<b>${pageLabels.annualFee}</b> ${cardData.annualFee}`
);

export const getScrollText = createSelector(
  [getPageFirstCardData],
  cardData => cardData?.hero?.buttons?.scrollDown?.text ?? ''
);

export const getScrollDownIcon = createSelector(
  [getPageFirstCardData],
  cardData => cardData?.hero?.buttons?.scrollDown?.imageName
);

export const getBackgroundImages = createSelector([getPageFirstCardData], cardData =>
  parseArrayToObject(cardData?.hero?.assets ?? [], 'breakpoint')
);

export const getDefaultTier1OfferBadgeText = createSelector(
  [getPageFirstCardData],
  cardData => {
    return cardData.defaultOfferButtonText;
  }
);

export const getOverlayOpacity = createSelector(
  [getPageFirstCardData],
  cardData => cardData?.hero?.overlay?.opacity ?? '0.54'
);

export const getOverlayColor = createSelector(
  [getPageFirstCardData],
  cardData => cardData?.hero?.overlay?.color ?? 'rgb(0,0,0)'
);
